import { QueryClient } from "@tanstack/react-query";
import type { AppType } from "@vetbolso/api/app";
import { hc } from "hono/client";

/** Cliente tipado da API (Hono RPC). Mesmo domínio: o cookie de sessão segue sozinho. */
export const api = hc<AppType>("/", { init: { credentials: "same-origin" } }).api.admin;

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
    readonly detalhes?: { campo: string; msg: string }[],
  ) {
    super(mensagem);
  }
}

type Resposta = { ok: boolean; status: number; json(): Promise<unknown> };
type Corpo<R> = R extends { json(): Promise<infer T> } ? T : never;

/** Lê a resposta; erro vira ErroApi com a mensagem do servidor. O tipo é o do corpo de sucesso. */
export async function ler<R extends Resposta>(p: Promise<R>): Promise<Exclude<Corpo<R>, { erro: string }>> {
  const r = await p;
  if (!r.ok) {
    const b = (await r.json().catch(() => ({}))) as { erro?: string; msg?: string; detalhes?: { campo: string; msg: string }[] };
    if (r.status === 401 && !location.pathname.startsWith("/login"))
      location.assign(`/login?volta=${encodeURIComponent(location.pathname)}`);
    throw new ErroApi(
      r.status,
      b.erro ?? "erro",
      b.msg ?? b.detalhes?.map((d) => `${d.campo}: ${d.msg}`).join("; ") ?? b.erro ?? `erro ${r.status}`,
      b.detalhes,
    );
  }
  return (await r.json()) as Exclude<Corpo<R>, { erro: string }>;
}

async function postAuth(caminho: string, corpo: unknown) {
  const r = await fetch(`/api/auth${caminho}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(corpo),
  });
  if (!r.ok) {
    const b = (await r.json().catch(() => ({}))) as { message?: string };
    throw new ErroApi(r.status, "auth", b.message ?? "não foi possível entrar");
  }
}
export const entrar = (email: string, password: string) => postAuth("/sign-in/email", { email, password });
export const sair = () => postAuth("/sign-out", {});

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, retry: (n, e) => !(e instanceof ErroApi && e.status < 500) && n < 2 } },
});
